import { useMemo } from "react";
import { useQuestingGroups } from "./useQuestingGroups";
import { useSchedulerAttendance } from "../features/dashboard/hooks/useSchedulerAttendance";
import { useUserProfilesByIds } from "./useUserProfiles";
import { useUserSettings } from "./useUserSettings";
import { buildAttendanceSummary } from "../features/dashboard/lib/attendance";
import { resolveDisplayTimeZone, shouldShowTimeZone } from "../lib/time";
import { normalizeEmail } from "../lib/utils";

/**
 * Reusable hook that enriches raw scheduler documents with slots, votes,
 * participant profiles, attendance summaries, and timezone info — the same
 * enrichment that DashboardPage performs, but extracted for reuse on
 * profile pages.
 */
export function useEnrichedSchedulers(schedulers, currentUserId) {
  const { groups } = useQuestingGroups();
  const { settings } = useUserSettings();

  const groupMembersById = useMemo(() => {
    const map = new Map();
    (groups || []).forEach((group) => {
      const members = (group.memberIds || []).filter(Boolean);
      map.set(group.id, members);
    });
    return map;
  }, [groups]);

  // Collect all participant + group-member IDs for profile lookup
  const allParticipantIds = useMemo(() => {
    const ids = new Set();
    (schedulers || []).forEach((scheduler) => {
      (scheduler.participantIds || []).forEach((id) => {
        if (id) ids.add(id);
      });
      if (scheduler.questingGroupId) {
        const groupMembers = groupMembersById.get(scheduler.questingGroupId) || [];
        groupMembers.forEach((id) => {
          if (id) ids.add(id);
        });
      }
    });
    return Array.from(ids);
  }, [schedulers, groupMembersById]);

  const { profiles: participantProfilesById } = useUserProfilesByIds(allParticipantIds);

  const { slotsByScheduler, votesByScheduler, votersByScheduler, loading: attendanceLoading } =
    useSchedulerAttendance(schedulers || [], currentUserId || null);

  const showTimeZone = useMemo(() => shouldShowTimeZone(settings), [settings]);

  const enrichedSchedulers = useMemo(() => {
    return (schedulers || []).map((scheduler) => {
      const slots = slotsByScheduler[scheduler.id] || [];
      const voteDocs = votesByScheduler[scheduler.id] || [];
      const winningSlot = scheduler.winningSlotId
        ? slots.find((s) => s.id === scheduler.winningSlotId)
        : null;

      const voters = (votersByScheduler[scheduler.id] || []).map((voter) => ({
        ...voter,
        email: voter.email ? normalizeEmail(voter.email) : voter.email,
      }));

      const groupMemberIds = scheduler.questingGroupId
        ? groupMembersById.get(scheduler.questingGroupId) || []
        : [];
      const participantIds = Array.from(
        new Set(
          [...(scheduler.participantIds || []), ...groupMemberIds].filter(Boolean)
        )
      );
      const participantProfiles = participantIds
        .map((id) => participantProfilesById[id])
        .filter(Boolean);
      const participantEmails = Array.from(
        new Set(
          participantProfiles
            .map((profile) => normalizeEmail(profile.email))
            .filter(Boolean)
        )
      );
      const participantEmailById = new Map(
        participantProfiles
          .filter((profile) => profile?.email)
          .map((profile) => [profile.id, normalizeEmail(profile.email)])
      );

      const pollPriorityAtMs =
        (scheduler.finalizedSlotPriorityAtMs && scheduler.winningSlotId
          ? scheduler.finalizedSlotPriorityAtMs[scheduler.winningSlotId]
          : null) ?? scheduler.finalizedAtMs ?? null;
      const { confirmed, maybe, unavailable } = buildAttendanceSummary({
        schedulerId: scheduler.id,
        status: scheduler.status,
        winningSlotId: scheduler.winningSlotId,
        winningSlotStart: winningSlot?.start || null,
        winningSlotEnd: winningSlot?.end || null,
        pollPriorityAtMs,
        busyByUserId: participantProfilesById,
        voteDocs,
        participantEmailById,
      });

      const respondedIds = voteDocs.map((voteDoc) => voteDoc.id).filter(Boolean);
      const respondedSet = new Set(respondedIds);
      const unresponded = participantIds
        .filter((id) => !respondedSet.has(id))
        .map((id) => participantEmailById.get(id))
        .filter(Boolean);

      const displayTimeZone = resolveDisplayTimeZone({
        pollTimeZone: scheduler.timezone,
        settings,
      });

      return {
        ...scheduler,
        effectiveParticipants: participantEmails,
        effectiveParticipantIds: participantIds,
        winningSlot,
        slots,
        voters,
        displayTimeZone,
        showTimeZone,
        attendanceSummary: {
          confirmed,
          maybe,
          unavailable,
          unresponded,
        },
      };
    });
  }, [
    schedulers,
    slotsByScheduler,
    votesByScheduler,
    votersByScheduler,
    groupMembersById,
    participantProfilesById,
    settings,
    showTimeZone,
  ]);

  return { enrichedSchedulers, loading: attendanceLoading };
}
